/**
 * Form Schema (v2 / Puck-based authoring)
 *
 * The FormSchema is a Puck `Data`-shape object persisted on `Form.schema` when
 * `Form.shapeVersion === V2_PUCK`. The renderer reads this schema directly;
 * there is no intermediate translation layer between authoring and runtime.
 *
 * Block ids are stable cuids generated client-side at insert time. Submissions
 * key answers by these ids, so ids must NOT be regenerated on edit.
 *
 * `showIf` is the only conditional logic primitive in v1 of the upgrade — see
 * the project plan for deferred extensions (skip chains, calculated fields).
 */

import { z } from 'zod'

// ============================================
// showIf condition
// ============================================

/**
 * A single conditional reference. The renderer hides the field when the
 * condition resolves to false; hidden fields are excluded from validation
 * AND from the submission payload (server re-evaluates this against the
 * stored schema snapshot to prevent client-side bypass).
 *
 * `op` semantics:
 *   - equals       — answer === value (string compare)
 *   - not_equals   — answer !== value
 *   - in           — value is string[]; answer is one of value
 *   - not_empty    — answer is present + non-empty (value ignored)
 */
export const ShowIfOpSchema = z.enum(['equals', 'not_equals', 'in', 'not_empty'])
export type ShowIfOp = z.infer<typeof ShowIfOpSchema>

export const ShowIfConditionSchema = z.object({
  fieldId: z.string().min(1),
  op: ShowIfOpSchema,
  value: z.union([z.string(), z.array(z.string())]).optional()
})
export type ShowIfCondition = z.infer<typeof ShowIfConditionSchema>

// ============================================
// Block-base props (shared across all field blocks)
// ============================================

const BlockBaseProps = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  required: z.boolean().optional(),
  helpText: z.string().optional(),
  showIf: ShowIfConditionSchema.optional()
})

// ============================================
// Per-block prop schemas (Puck-block-shaped: { type, props })
// ============================================

const ShortTextBlockSchema = z.object({
  type: z.literal('ShortText'),
  props: BlockBaseProps.extend({
    placeholder: z.string().optional(),
    maxLength: z.number().int().positive().optional()
  })
})

const LongTextBlockSchema = z.object({
  type: z.literal('LongText'),
  props: BlockBaseProps.extend({
    placeholder: z.string().optional(),
    maxLength: z.number().int().positive().optional()
  })
})

const SelectBlockSchema = z.object({
  type: z.literal('Select'),
  props: BlockBaseProps.extend({
    options: z.array(z.string().min(1)).min(1)
  })
})

const MultiSelectBlockSchema = z.object({
  type: z.literal('MultiSelect'),
  props: BlockBaseProps.extend({
    options: z.array(z.string().min(1)).min(1)
  })
})

const CheckboxBlockSchema = z.object({
  type: z.literal('Checkbox'),
  props: BlockBaseProps
})

const DateBlockSchema = z.object({
  type: z.literal('Date'),
  props: BlockBaseProps
})

const PhoneBlockSchema = z.object({
  type: z.literal('Phone'),
  props: BlockBaseProps
})

const EmailBlockSchema = z.object({
  type: z.literal('Email'),
  props: BlockBaseProps
})

const SignatureBlockSchema = z.object({
  type: z.literal('Signature'),
  props: BlockBaseProps.extend({
    // Only typed signatures ship this round; drawn/canvas is a next-step. The
    // literal keeps the door open for `'drawn'` without a schema break.
    signatureType: z.literal('typed').optional(),
    // When true the fill experience collects the signer's name + email and the
    // backend requires them for a consent-bearing form (R5/R7). A *required*
    // Signature block is what makes a form consent-bearing (KTD1); this prop
    // governs identity capture, independent of that predicate.
    requireSignerIdentity: z.boolean().optional()
  })
})

const FileUploadBlockSchema = z.object({
  type: z.literal('FileUpload'),
  props: BlockBaseProps.extend({
    // MIME-type accept list (e.g., ['image/*', 'application/pdf']).
    accept: z.array(z.string()).optional(),
    // Max bytes per file (renderer + server enforce).
    maxBytes: z.number().int().positive().optional()
  })
})

// Repeating section nests a *flat* list of child blocks. The renderer
// duplicates the children per row added by the submitter. Nested
// RepeatingSections are not supported in v1 — keeps validation tractable.
export const RepeatingChildBlockSchema = z.union([
  ShortTextBlockSchema,
  LongTextBlockSchema,
  SelectBlockSchema,
  MultiSelectBlockSchema,
  CheckboxBlockSchema,
  DateBlockSchema,
  PhoneBlockSchema,
  EmailBlockSchema,
  FileUploadBlockSchema
])
export type RepeatingChildBlock = z.infer<typeof RepeatingChildBlockSchema>

const RepeatingSectionBlockSchema = z.object({
  type: z.literal('RepeatingSection'),
  props: BlockBaseProps.extend({
    minRows: z.number().int().nonnegative().optional(),
    maxRows: z.number().int().positive().optional(),
    // Renamed from `children` to avoid colliding with Puck's reserved
    // `children`-slot inference in the web form builder. Schema-wise the
    // semantics are unchanged: a flat ordered list of row-template fields.
    rowChildren: z.array(RepeatingChildBlockSchema).min(1)
  })
})

const PageBreakBlockSchema = z.object({
  type: z.literal('PageBreak'),
  props: z.object({
    id: z.string().min(1),
    title: z.string().optional()
  })
})

const SectionHeaderBlockSchema = z.object({
  type: z.literal('SectionHeader'),
  props: z.object({
    id: z.string().min(1),
    text: z.string().min(1),
    level: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional()
  })
})

const MarkdownBlockSchema = z.object({
  type: z.literal('Markdown'),
  props: z.object({
    id: z.string().min(1),
    body: z.string()
  })
})

// ============================================
// FormBlock discriminated union
// ============================================

export const FormBlockSchema = z.discriminatedUnion('type', [
  ShortTextBlockSchema,
  LongTextBlockSchema,
  SelectBlockSchema,
  MultiSelectBlockSchema,
  CheckboxBlockSchema,
  DateBlockSchema,
  PhoneBlockSchema,
  EmailBlockSchema,
  SignatureBlockSchema,
  FileUploadBlockSchema,
  RepeatingSectionBlockSchema,
  PageBreakBlockSchema,
  SectionHeaderBlockSchema,
  MarkdownBlockSchema
])
export type FormBlock = z.infer<typeof FormBlockSchema>

export type FormBlockType = FormBlock['type']

/**
 * The form block `type` discriminants as a plain readonly tuple. Mirrors the
 * `FormBlockSchema` discriminated-union members exactly (asserted in the
 * shared-types parity test). Exported for symmetry with pages'
 * `PUCK_COMPONENT_TYPES` and for the Puck→FormSchema normalizer.
 */
export const FORM_BLOCK_TYPES = [
  'ShortText',
  'LongText',
  'Select',
  'MultiSelect',
  'Checkbox',
  'Date',
  'Phone',
  'Email',
  'Signature',
  'FileUpload',
  'RepeatingSection',
  'PageBreak',
  'SectionHeader',
  'Markdown'
] as const
export type FormBlockTypeName = (typeof FORM_BLOCK_TYPES)[number]

// ============================================
// FormSchema (Puck Data wrapper)
// ============================================

// ============================================
// Ending screen (lives in root.props — no DB migration)
// ============================================

/**
 * Hosts an `endingScreen.redirectUrl` may point at, beyond relative
 * same-origin paths. Absolute redirects are restricted to School Journey
 * properties — arbitrary external redirects are intentionally NOT supported
 * in v1 (open-redirect guard). A respondent on the public form route must
 * never be sent to an attacker-controlled origin by an admin-authored value.
 */
export const ALLOWED_ABSOLUTE_REDIRECT_HOSTS = ['schooljourney.ai', 'schooljourney.com'] as const

/**
 * Open-redirect guard shared by the Zod schema (save-time) and the public
 * form client (before `router.push`). Accepts:
 *   - relative same-origin paths (`/thanks`) — not protocol-relative (`//`)
 *     and not backslash-smuggled (`/\`),
 *   - absolute https URLs whose host is (a subdomain of) an allowlisted host.
 */
export function isSafeRedirectUrl(value: string): boolean {
  if (typeof value !== 'string' || value.length === 0) return false
  if (value.startsWith('//') || value.startsWith('/\\')) return false
  if (value.startsWith('/')) return true
  try {
    const url = new URL(value)
    if (url.protocol !== 'https:') return false
    return ALLOWED_ABSOLUTE_REDIRECT_HOSTS.some(h => url.hostname === h || url.hostname.endsWith(`.${h}`))
  } catch {
    return false
  }
}

export const EndingScreenSchema = z.object({
  thankYouMessage: z.string().max(2000).optional(),
  redirectUrl: z
    .string()
    .refine(isSafeRedirectUrl, {
      message: 'redirectUrl must be a relative path or an allowlisted https URL'
    })
    .optional(),
  // Clamp to a sane range; the client waits this many seconds before redirect.
  redirectDelaySeconds: z.number().int().min(0).max(30).optional()
})
export type EndingScreen = z.infer<typeof EndingScreenSchema>

export const FormSchemaRootSchema = z.object({
  props: z.object({
    title: z.string().min(1),
    // Declared so plain-Zod parse (backend re-parses every save) does NOT
    // strip it — this declaration is Gate 1, the chokepoint for persisting
    // the ending screen with no DB migration.
    endingScreen: EndingScreenSchema.optional()
  })
})

export const FormSchemaSchema = z.object({
  content: z.array(FormBlockSchema),
  root: FormSchemaRootSchema
})
export type FormSchema = z.infer<typeof FormSchemaSchema>

// ============================================
// Submission payload
// ============================================

/**
 * Answer values accepted in a submission payload. Strings cover most field
 * types (short text, long text, select, date ISO string, phone, email,
 * signature data url). string[] covers MultiSelect. Records cover File
 * (`{ key, url, mimeType, size }`). Arrays of records cover RepeatingSection.
 */
export const FormAnswerValueSchema: z.ZodType<unknown> = z.lazy(() =>
  z.union([
    z.string(),
    z.array(z.string()),
    z.boolean(),
    z.number(),
    z.record(z.string(), z.unknown()),
    z.array(z.record(z.string(), z.unknown()))
  ])
)
export type FormAnswerValue = z.infer<typeof FormAnswerValueSchema>

export const FormPayloadSchema = z.record(z.string(), FormAnswerValueSchema)
export type FormPayload = z.infer<typeof FormPayloadSchema>
